import * as Sentry from "@sentry/react";
import {
  type EnsureQueryDataOptions,
  isCancelledError,
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { isHTTPError } from "ky";
import { isNetworkFailure } from "@/lib/server-errors";

function reportToSentry(error: unknown) {
  // 422s are recoverable validation errors surfaced to the user as field
  // errors; not show-stoppers worth paging on.
  if (isHTTPError(error) && error.response.status === 422) return;
  // A dropped connection is the applicant's network, not something we can fix.
  if (isNetworkFailure(error)) return;
  Sentry.captureException(error);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportToSentry }),
  mutationCache: new MutationCache({ onError: reportToSentry }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: import.meta.env.MODE === "test" ? false : 1,
    },
  },
});

/**
 * Runs `read` once more when it is cancelled. A read whose last observer
 * leaves mid-fetch is cancelled, and a caller that joined it sees the cancel
 * even though it still wants the data. A page unmounting for any reason, such
 * as a conditional render dropping its component, can do that to a read the
 * next route's loader joined.
 *
 * The session purge cancels reads too, by removing their queries, and a re-ask
 * would only earn a 401. Cancelling alone can't tell the two apart, but a
 * purged query is gone from the cache while an abandoned one stays. So the
 * cancel is rethrown unless `queryKey` is still cached.
 */
export async function retryIfCancelled<T>(
  queryKey: QueryKey,
  read: () => Promise<T>,
): Promise<T> {
  try {
    return await read();
  } catch (error) {
    if (!isCancelledError(error)) throw error;
    if (!queryClient.getQueryCache().find({ queryKey, exact: true })) {
      throw error;
    }
    return read();
  }
}

export async function ensureConsumerQueryData<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>) {
  try {
    return await retryIfCancelled(options.queryKey, () =>
      queryClient.ensureQueryData(options),
    );
  } catch (error) {
    if (isHTTPError(error) && error.response.status === 401) {
      return null;
    }

    throw error;
  }
}
